/** @module polyfills */

let eventOptionsSupport = null;

function checkSupport() {
    let passive = false,
        once = false,
        capture = false;

    try {
        let options = {
            get passive() {
                return (passive = true);
            },

            get once() {
                return (once = true);
            },

            get capture() {
                return (capture = true);
            },
        };

        window.addEventListener('test', options, options);
        window.removeEventListener('test', options, options);
    } catch (err) {
        // nothing to do
    }

    eventOptionsSupport = {
        passive,
        once,
        capture,
    };
}

/**
 * Checks if the intended options are supported by the browser and returns the correct value.
 *
 * @param {object} options - For more information see [MDN]{@link https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Parameters}.
 * @param {boolean} [options.passive=false] - Whether to use passive mode.
 * @param {boolean} [options.once=false] - Whether to remove the handler upon first execution.
 * @param {boolean} [options.capture=false] - Whether use capture mode.
 *
 * @returns {boolean|EventListenerOptions} - A boolean value based on <code>options.capture</code> if the browser does not support EventHandlerOptions, the intended <code>EventHandlerOptions</code> otherwise.
 *
 * @example
 * import { eventListenerOptions } from 'path/to/passive-event-handler';
 *
 * window.addEventListener('event', handlerFn, eventListenerOptions({ passive: true, capture: false }));
 *
 * // on non supporting environments (IE), this will be the same as
 * window.addEventListener('event', handlerFn, false);
 */
export function eventListenerOptions({ passive = false, once = false, capture = false } = {}) {
    if (eventOptionsSupport === null) {
        checkSupport();
    }

    if (passive && !eventOptionsSupport.passive) {
        return capture;
    }

    if (once && !eventOptionsSupport.once) {
        return capture;
    }

    if (!eventOptionsSupport.capture) {
        return capture;
    }

    return {
        passive,
        once,
        capture,
    };
}
